import { Button, Container, Icon, PlaidContainer } from "components";
import styles from "./achPayment.module.scss";
import { DropdownFormField, InputFormField } from "components/hookForm";
import { useFormContext } from "react-hook-form";
import { TypoGraph } from "components";
import { useCallback, useMemo, useState } from "react";
import { PlaidLinkOnSuccessMetadata, PlaidLinkOptions } from "react-plaid-link";
import localStorageKeys from "constants/localStorageKeys";
import strings from "strings";
import { getPlaidLinkTokenPublic } from "api";
import {
  passportAccountHolderTypes,
  passportAccountTypes,
} from "constants/dropdownOptions";

interface IAchPaymentProps {}

interface PlaidAccount {
  id: string;
  name: string;
  mask: string;
  type: string;
  subtype: string;
  verification_status: string | null;
}

const AchPayment: React.FC<IAchPaymentProps> = (props) => {
  const { setValue, getValues, setError, formState } = useFormContext();
  const [publicToken, setPublicToken] = useState<string>("");
  const [showPlaid, setShowPlaid] = useState<boolean>(false);
  const [showPlaidAssociation, setShowPlaidAssociation] =
    useState<boolean>(true);
  const [linkToken, setLinkToken] = useState<string>("");
  const [isLoadingToken, setIsLoadingToken] = useState<boolean>(false);
  const [availableAccounts, setAvailableAccounts] = useState<PlaidAccount[]>(
    []
  );
  const [selectedAccount, setSelectedAccount] = useState<PlaidAccount | null>(
    null
  );

  const [hasEverHadAccounts, setHasEverHadAccounts] = useState<boolean>(false);
  const [institutionInfo, setInstitutionInfo] = useState<any>(null);

  // List of institutions that require routing number
  const institutionsRequiringRoutingNumber = [
    { name: "PNC", institution_id: "ins_13" },
    { name: "Chase", institution_id: "ins_56" },
    { name: "U.S. Bank", institution_id: "ins_127990" },
  ];

  // Check if current institution requires routing number
  const requiresRoutingNumber = () => {
    if (!institutionInfo) return false;
    return institutionsRequiringRoutingNumber.some(
      (inst) => inst.institution_id === institutionInfo.institution_id
    );
  };

  const validatePlaidAccount = () => {
    if (!selectedAccount || !publicToken) {
      setError("plaidAccount", {
        type: "required",
        message:
          "Please associate your bank account using Plaid and select an account.",
      });
      return false;
    }
    return true;
  };

  const handlePlaidSuccess = async (
    public_token: string,
    metadata: PlaidLinkOnSuccessMetadata
  ) => {
    setPublicToken(public_token);
    setShowPlaid(false); // Hide Plaid after success
    // Store the available accounts
    if (metadata.accounts && metadata.accounts.length > 0) {
      setAvailableAccounts(metadata.accounts);
      setShowPlaidAssociation(false);
      setHasEverHadAccounts(true); // Mark that we've had accounts
      setSelectedAccount(metadata.accounts[0]);
      setValue("publicAccessToken", public_token);
      setValue("accountId", metadata.accounts[0].id);
      // Store institution information
      if (metadata.institution) {
        setInstitutionInfo(metadata.institution);
      }
    } else {
      setError("plaidAccount", {
        type: "required",
        message: "No bank accounts found. Please try again or contact support.",
      });
    }
  };

  const handleAccountSelection = async (account: PlaidAccount) => {
    setSelectedAccount(account);
    setValue("publicAccessToken", publicToken);
    setValue("accountId", account.id);
  };

  const handlePlaidExit = (err: any, metadata: any) => {
    setShowPlaid(false);
  };

  const getPlaidToken = useCallback(async () => {
    setIsLoadingToken(true);
    try {
      const response = await getPlaidLinkTokenPublic();
      const token = response.data.token;
      setLinkToken(token);
      localStorage.setItem(localStorageKeys.plaidLinkToken, token);
      return token;
    } catch (error) {
      return null;
    } finally {
      setIsLoadingToken(false);
    }
  }, []);

  const handlePlaidButtonClick = async () => {
    const token = await getPlaidToken();
    if (token) {
      setShowPlaid(true);
    }
  };

  const config: PlaidLinkOptions = {
    onSuccess: handlePlaidSuccess,
    onExit: handlePlaidExit,
    token: linkToken,
  };

  // Use useMemo to prevent unnecessary re-renders and ensure accounts stay visible
  const shouldShowPlaid = useMemo(() => {
    // Only show Plaid if we've never had accounts OR if we currently have no accounts
    return !hasEverHadAccounts || availableAccounts.length === 0;
  }, [availableAccounts.length, hasEverHadAccounts]);

  const clearAccounts = () => {
    setAvailableAccounts([]);
    setSelectedAccount(null);
    setPublicToken("");
    setHasEverHadAccounts(false);
    setInstitutionInfo(null);
  };

  const renderAccountSelectionList = () => (
    <Container className={styles.accountSelectionContainer}>
      <p
        style={{ textAlign: "center", marginBottom: "1rem", fontWeight: "500" }}
      >
        Select a bank account to use:
      </p>
      {availableAccounts.map((account) => (
        <Container key={account.id} className={styles.accountOption}>
          <div className={styles.accountButtonWrapper}>
            <Button
              variant={
                selectedAccount?.id === account.id ? "contained" : "outlined"
              }
              label={`${account.name} (${account.mask}) - ${account.subtype}`}
              onClick={() => handleAccountSelection(account)}
              className={styles.accountButton}
            />
          </div>
        </Container>
      ))}
      <div style={{ textAlign: "center", marginTop: "1rem" }}>
        <Button
          variant="text"
          label="Clear and start over"
          onClick={clearAccounts}
          className={styles.clearButton}
        />
      </div>
    </Container>
  );

  return (
    <Container className={styles.achDetailsContainer}>
      {shouldShowPlaid ? (
        <Container className={styles.plaidSection}>
          <Container className={styles.logoContainer}>
            <Icon name="plaid" type="images" height={41} />
          </Container>
          <p style={{ textAlign: "center" }}>
            Securely add payment information via Plaid
          </p>
          <Button
            id={strings.DISBURSEMENT_ACCOUNT_STRING.ASSOCIATE_BANK_ACCOUNT}
            variant="contained"
            label={strings.DISBURSEMENT_ACCOUNT_STRING.ASSOCIATE_BANK_ACCOUNT}
            onClick={handlePlaidButtonClick}
            loading={isLoadingToken}
            disabled={isLoadingToken || showPlaid}
          />
          {showPlaid && linkToken && <PlaidContainer options={config} />}
        </Container>
      ) : (
        renderAccountSelectionList()
      )}

      {/* Display Plaid validation error */}
      {formState.errors.plaidAccount && (
        <Container sx={{ marginTop: "0.5rem", marginBottom: "1rem" }}>
          <TypoGraph
            variant="body2"
            content={String(
              formState.errors.plaidAccount.message ||
                "Please associate your bank account using Plaid."
            )}
            sx={{ color: "#d32f2f", textAlign: "center" }}
          />
        </Container>
      )}

      {/* Hidden field for Plaid validation */}
      <div style={{ display: "none" }}>
        <InputFormField
          name="plaidAccount"
          label=""
          validate={validatePlaidAccount}
        />
      </div>

      <InputFormField
        name="accountHolder"
        label="Account Holder Name"
        required
      />
      <Container className={styles.paymentDetails}>
        <DropdownFormField
          name={"bankAccountType"}
          label={"Account Type"}
          options={passportAccountTypes}
          required
        ></DropdownFormField>
        <DropdownFormField
          name={"holderType"}
          label={"Holder Type"}
          options={passportAccountHolderTypes}
          required
        ></DropdownFormField>
      </Container>

      <Container className={styles.paymentDetails}></Container>
      {requiresRoutingNumber() && (
        <Container className={styles.paymentDetails}>
          <InputFormField
            name="accountNumber"
            label="Account Number"
            hideableText={true}
            required
          />
          <InputFormField
            name="accountNumberConfirmation"
            label="Confirm Account Number"
            hideableText={true}
            required
            validate={(value: any) =>
              value === getValues("accountNumber") ||
              "Account Numbers must match."
            }
          />
          <InputFormField
            name="routingNumber"
            label="Electronic Routing Number"
            hideableText={true}
            required
          />
          <InputFormField
            name="routingNumberConfirmation"
            label="Confirm Electronic Routing Number"
            hideableText={true}
            required
            validate={(value: any) =>
              value === getValues("routingNumber") ||
              "Electronic Routing Number must match."
            }
          />
        </Container>
      )}
    </Container>
  );
};

export default AchPayment;
